/**
 * @file useUploadBanner.js
 * @module src/js/hooks/useUploadBanner
 * @description
 * **A hook to upload banner images via POST request.**
 * - Utilizes React contexts to manage modal messages.
 * - Returns a function that:
 *  - Sends new banner image files to the backend.
 *   - Handles FormData for file uploads.
 *   - Updates the local banner data state upon successful upload.
 *   - Utilizes modal messages to indicate upload status.
 * 
 * @version
 * <p>
 *  2025-12-02: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Migrate to useUploadBanner hook. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Rename files to avoid name conflicts. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useModal } from "../contexts/ModalProvider";
import useFetchFromBackend from "./useFetchFromBackend";

export default function useUploadBanner() {
    const { pathname } = useLocation();
    const { showModalMessage } = useModal();
    const fetchFromBackend = useFetchFromBackend();

    const uploadBanner = useCallback(async ({
        files,
        setData,
        setUploading,
        setShowUploadForm,
    }) => {
        if (files == null || files.length === 0 ||
            typeof setData !== "function" ||
            typeof setUploading !== "function" ||
            typeof setShowUploadForm !== "function"
        ) {
            throw new Error("files, setData, setUploading, and setShowUploadForm must be provided to uploadBanner.");
        }

        try {
            setUploading(true);
            showModalMessage({ type: "info", content: "Uploading..." });

            let formData = new FormData();
            if (files.length > 0) {
                // Rename files to avoid name conflicts
                files.forEach((file, index) => {
                    if (!file) return;
                    const newName = `${index}_${file.name}`;
                    formData.append("file[]", file, newName);
                });
            }

            const data = await fetchFromBackend({
                request: "/api/banners",
                method: "POST",
                body: formData,
                uploadFile: true,
                requireAuth: true,
                from: pathname,
            });

            if (data && data.success) {
                const data = await fetchFromBackend({ request: "/api/banners" });
                setData(data || []);
                setShowUploadForm(false);
                showModalMessage({ type: "success", content: "Successfully uploaded!" });

            } else if (data && data.error) {
                console.error("Failed to upload:", data.error);
                showModalMessage({ type: "error", content: "Failed to upload. Please try again." });
            }

        } catch (error) {
            console.error("An error occurred:", error);
            showModalMessage({ type: "error", content: "An error occurred. Please try again." });

        } finally {
            setUploading(false);
        }
    }, [fetchFromBackend, pathname, showModalMessage]);
    
    return uploadBanner;
}